import React, { useState } from 'react';
import { AcceptRejectDialog, NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { deleteFilterGroupFromChartFilterGroup } from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { deleteFilterGroupFromDashBoardFilterGroup } from '../../../redux/DashBoardFilterGroup/DashBoardFilterGroupAction';
import { useDispatch, useSelector } from 'react-redux';
import { callQueryAPI, SetModifyOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { RootState } from '../../../redux';

interface Props {
  filterGroupToBeDeleted: React.MutableRefObject<{
    id: string;
    name: string;
  } | null>;
  datasetGroupList: string[];
  setAnchorEl: React.Dispatch<React.SetStateAction<Element | null>>;
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * @returns dialog box on clicking delete icon in ChartFilterMenu.tsx
 * Responsible to delete a filter group based on certain conditions.
 */
const DeleteFilterGroupDialog = ({
  filterGroupToBeDeleted,
  datasetGroupList,
  setAnchorEl,
  openDialog,
  setOpenDialog,
}: Props) => {
  const [openAlert, setOpenAlert] = useState(false);
  const { chartGroup, dashBoardGroup } = useSelector((state: RootState) => ({
    chartGroup: state.chartFilterGroup,
    dashBoardGroup: state.dashBoardFilterGroup,
  }));
  const dispatch = useDispatch();

  /**
   * selected FilterGroup  will be deleted from chartFilterGroup.groups
   * For  each tiles in chartFilterGroup.tabTile if selected filterGroup is in any tile then it will be deleted from that tile
   */
  const deleteFilterGroup = () => {
    if (!filterGroupToBeDeleted.current) return;
    if (datasetGroupList.length === 1) {
      setAnchorEl(null);
      setOpenDialog(false);
      setOpenAlert(true);
      return;
    }
    Object.keys(chartGroup.tabTile).forEach((tabTileId: string) => {
      if (
        filterGroupToBeDeleted.current?.id &&
        dashBoardGroup.filterGroupTabTiles[filterGroupToBeDeleted.current?.id]?.includes(tabTileId)
      ) {
        if (chartGroup.groups[filterGroupToBeDeleted.current?.id]?.filters?.length > 0) {
          dispatch(SetModifyOptions(tabTileId, 'axis', null));
        }
        dispatch(callQueryAPI(tabTileId, true));
      }
    });
    dispatch(callQueryAPIDashboard(true));
    Object.entries(chartGroup.tabTile).forEach(([tabTileId, arr]: any) => {
      if (arr.includes(filterGroupToBeDeleted.current?.id)) {
        if (
          filterGroupToBeDeleted.current?.id &&
          chartGroup.groups[filterGroupToBeDeleted.current?.id]?.filters?.length > 0
        ) {
          dispatch(SetModifyOptions(tabTileId, 'axis', null));
        }
        dispatch(callQueryAPI(tabTileId, true));
      }
    });
    dispatch(deleteFilterGroupFromChartFilterGroup(filterGroupToBeDeleted.current.id));
    dispatch(deleteFilterGroupFromDashBoardFilterGroup(filterGroupToBeDeleted.current.id));
    filterGroupToBeDeleted.current = null;
    setOpenDialog(false);
    setAnchorEl(null);
  };

  return (
    <>
      <AcceptRejectDialog
        varient="animated"
        open={openDialog}
        acceptFunction={deleteFilterGroup}
        rejectFunction={() => {
          setOpenDialog(false);
          filterGroupToBeDeleted.current = null;
        }}
        closeFunction={() => {
          filterGroupToBeDeleted.current = null;
          setOpenDialog(false);
        }}
        heading="Are You Sure?"
        messages={[
          {
            text: `Are you sure you want to delete ${
              filterGroupToBeDeleted.current?.name || 'This Filter Group'
            }?`,
            highlights: [
              {
                substring: filterGroupToBeDeleted.current?.name || 'This Filter Group',
                style: { fontWeight: 'bold' },
              },
            ],
          },
          {
            text: 'This action cannot be undone.',
          },
        ]}
        acceptText="Delete"
        rejectText="Cancel"
        acceptTransition={{
          transitionTime: '0.2s',
          backgroundColor: 'secondary.contrastText',
          color: 'red',
          hoverBackgroundColor: 'red',
          hoverColor: 'secondary.contrastText',
        }}
        rejectTransition={{
          transitionTime: '0.2s',
          backgroundColor: 'secondary.contrastText',
          color: 'primary.contrastText',
          hoverBackgroundColor: 'primary.contrastText',
          hoverColor: 'secondary.contrastText',
        }}
      />

      <NotificationDialog
        openAlert={openAlert}
        severity={'warning'}
        testMessage={'Cannot delete filterGroup. Atleast one filter group should be there'}
        onCloseAlert={() => {
          setOpenAlert(false);
          setAnchorEl(null);
        }}
      />
    </>
  );
};

export default DeleteFilterGroupDialog;
